/**
 * Experimental tool support for testing unmerged PRs and staging branches
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import * as os from 'os';
import {debug} from '../utils/debug-logger';
import {
    InstalledTool,
    PackageManagerOptions
} from './types';

export interface ExperimentalTool extends InstalledTool {
    experimental: true;
    source: 'branch' | 'pr';
    sourceRef: string; // branch name or PR number
}

export interface ExperimentalManifest {
    version: string;
    experimental: ExperimentalTool[];
    lastUpdated: string;
}

export class ExperimentalToolManager {
    private manifestPath: string;
    private registryUrl: string;

    constructor(private options: PackageManagerOptions = {}) {
        const baseDir = options.toolsDir || path.join(os.homedir(), '.clanker', 'tools');
        this.manifestPath = path.join(baseDir, 'experimental-manifest.json');
        this.registryUrl = 'https://api.github.com/repos/zigglers/rachet';
    }

    /**
     * Check if experimental mode is enabled
     */
    async isEnabled(): Promise<boolean> {
        // Check environment variable
        if (process.env.CLANKER_EXPERIMENTAL === 'true') {
            return true;
        }

        // Check config file
        try {
            const configPath = path.join(os.homedir(), '.clanker', 'config.json');
            const config = await fs.readFile(configPath, 'utf-8');
            const parsed = JSON.parse(config);
            return parsed.experimental?.enabled === true;
        } catch {
            return false;
        }
    }

    /**
     * Enable experimental mode
     */
    async enable(): Promise<void> {
        const configPath = path.join(os.homedir(), '.clanker', 'config.json');
        let config: Record<string, unknown>;

        try {
            const existing = await fs.readFile(configPath, 'utf-8');
            config = JSON.parse(existing);
        } catch {
            config = {};
        }

        config.experimental = {
            enabled: true,
            autoUpgrade: true,
            sources: ['branches', 'pull-requests']
        };

        await fs.mkdir(path.dirname(configPath), {recursive: true});
        await fs.writeFile(configPath, JSON.stringify(config, null, 2));

        debug.log('[Experimental] Experimental mode enabled');
    }

    /**
     * List available experimental tools from branches
     */
    async listAvailable(): Promise<ExperimentalTool[]> {
        const tools: ExperimentalTool[] = [];

        try {
            // Fetch branches from GitHub API
            const response = await fetch(`${this.registryUrl}/branches`, {
                headers: {
                    'Accept': 'application/vnd.github.v3+json',
                    'User-Agent': 'clanker-cli'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch branches: ${response.statusText}`);
            }

            const branches = await response.json() as Record<string, never>[];

            // Filter for tool branches (format: org/tool@version)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const toolBranches = branches.filter((branch: any) =>
                branch.name.match(/^[^/]+\/[^/]+@[\d.]+$/)
            );

            for (const branch of toolBranches) {
                const fullName: string = branch.name;
                const [orgTool, version] = fullName.split('@');
                const [org, name] = orgTool.split('/');

                tools.push({
                    org,
                    name,
                    version,
                    installedAt: '',
                    path: '',
                    experimental: true,
                    source: 'branch',
                    sourceRef: branch.name
                });
            }

            // Also fetch open PRs
            const prResponse = await fetch(`${this.registryUrl}/pulls?state=open`, {
                headers: {
                    'Accept': 'application/vnd.github.v3+json',
                    'User-Agent': 'clanker-cli'
                }
            });

            if (prResponse.ok) {
                const prs = await prResponse.json() as Record<string, unknown>[];

                for (const pr of prs) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const head: unknown = (pr as any).head;
                    if (!head || typeof head !== 'object' || !('ref' in head)) {
                        continue; // Skip invalid PRs
                    }
                    // Check if PR head ref matches tool format (org/tool@version)
                    if (typeof head.ref !== 'string') {
                        continue; // Skip if ref is not a string
                    }
                    // Match PR head ref format: org/tool@version
                    // Example: zigglers/rachet@1.0.0
                    if (!head.ref.match(/^[^/]+\/[^/]+@[\d.]+$/)) {
                        continue; // Skip if not a tool submission
                    }
                    // Extract org, name, and version from PR head ref
                    const [orgTool, version] = head.ref.split('@');
                    const [org, name] = orgTool.split('/');
                    // Create tool entry
                    // Add tool to list
                    // This is a tool submission PR
                    // Experimental tools from PRs
                    // Example: zigglers/rachet@1.0.0
                    // This is a tool submission PR
                    tools.push({
                        org,
                        name,
                        version,
                        installedAt: '',
                        path: '',
                        experimental: true,
                        source: 'pr',
                        sourceRef: pr.number.toString()
                    });
                }
            }

        } catch (error) {
            debug.error('[Experimental] Failed to list available tools:', error);
        }

        return tools;
    }

    /**
     * Install an experimental tool
     */
    async install(toolSpec: string): Promise<void> {
        debug.log(`[Experimental] Installing experimental tool: ${toolSpec}`);

        // Parse tool spec (org/tool@version, org/tool@pr-123, org/tool@branch)
        let org: string, name: string, ref: string;
        let source: 'branch' | 'pr';

        if (toolSpec.includes('@pr-')) {
            // PR reference: org/tool@pr-123
            const [orgTool, prRef] = toolSpec.split('@pr-');
            [org, name] = orgTool.split('/');
            ref = prRef;
            source = 'pr';
        } else if (toolSpec.includes('@')) {
            // Branch reference: org/tool@branch-name
            const [orgTool, branchRef] = toolSpec.split('@');
            [org, name] = orgTool.split('/');
            ref = branchRef;
            source = 'branch';
        } else {
            throw new Error('Experimental tools must specify a version, branch, or PR number');
        }

        // Resolve to actual branch name
        let branchName: unknown = ref;
        if (source === 'pr') {
            // Get branch name from PR
            const response = await fetch(`${this.registryUrl}/pulls/${ref}`, {
                headers: {
                    'Accept': 'application/vnd.github.v3+json',
                    'User-Agent': 'clanker-cli'
                }
            });

            if (!response.ok) {
                throw new Error(`PR #${ref} not found`);
            }

            const pr = await response.json() as Record<string, unknown>;
            if (!pr.head || typeof pr.head !== 'object' || !('ref' in pr.head)) {
                throw new Error(`Invalid PR data for #${ref}`);
            }

            branchName = pr.head.ref;
        }

        // Download tool from branch
        const toolUrl = `https://raw.githubusercontent.com/zigglers/rachet/${branchName}/tools/${org}/${name}`;

        // Get latest version from branch
        const versionsResponse = await fetch(`${toolUrl}`, {
            headers: {'User-Agent': 'clanker-cli'}
        });

        if (!versionsResponse.ok) {
            throw new Error(`Tool ${org}/${name} not found in branch ${branchName}`);
        }
        const versionsContent = await versionsResponse.text();
        const versionMatch = versionsContent.match(/@([\d.]+)/);
        if (!versionMatch) {
            throw new Error(`No version found for tool ${org}/${name} in branch ${branchName}`);
        }
        const branchVersion = versionMatch[1];
        if (!branchVersion) {
            throw new Error(`No version found in branch name ${branchName}`);
        }
        // If the branch name is not in the expected format, we can assume it's experimental
        // For now, assume version is in the branch name

        // Download tool files
        const toolDir = path.join(
            this.options.toolsDir || path.join(os.homedir(), '.clanker', 'tools'),
            org,
            name,
            branchVersion
        );

        await fs.mkdir(toolDir, {recursive: true});

        // Download index.js
        const indexResponse = await fetch(`${toolUrl}/${branchVersion}/index.js`, {
            headers: {'User-Agent': 'clanker-cli'}
        });

        if (!indexResponse.ok) {
            throw new Error(`Failed to download tool files`);
        }

        const indexContent = await indexResponse.text();
        await fs.writeFile(path.join(toolDir, 'index.js'), indexContent);

        // Download manifest.json
        const manifestResponse = await fetch(`${toolUrl}/${branchVersion}/manifest.json`, {
            headers: {'User-Agent': 'clanker-cli'}
        });

        if (manifestResponse.ok) {
            const manifestContent = await manifestResponse.text();
            await fs.writeFile(path.join(toolDir, 'manifest.json'), manifestContent);
        }

        // Update experimental manifest
        await this.addToManifest({
            org,
            name,
            version: branchVersion,
            installedAt: new Date().toISOString(),
            path: toolDir,
            experimental: true,
            source,
            sourceRef: ref
        });

        console.log(`✅ Installed experimental tool: ${org}/${name}@${branchVersion} from ${source} ${ref}`);
    }

    /**
     * List installed experimental tools
     */
    async listInstalled(): Promise<ExperimentalTool[]> {
        try {
            const manifest = await this.loadManifest();
            return manifest.experimental;
        } catch {
            return [];
        }
    }

    /**
     * Upgrade experimental tools to stable versions
     */
    async upgradeToStable(): Promise<void> {
        const experimental = await this.listInstalled();
        let upgraded = 0;

        for (const tool of experimental) {
            try {
                // Check if tool is now in main registry
                const registryResponse = await fetch(
                    `https://raw.githubusercontent.com/zigglers/rachet/main/tools/${tool.org}/${tool.name}/metadata.json`,
                    {headers: {'User-Agent': 'clanker-cli'}}
                );

                if (registryResponse.ok) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const metadata = await registryResponse.json() as Record<string, any>;

                    // Check if our version is now stable
                    if (metadata.versions && metadata.versions[tool.version]) {
                        console.log(`📦 Upgrading ${tool.org}/${tool.name}@${tool.version} to stable`);

                        // Remove experimental flag
                        await this.removeFromManifest(tool);

                        // Tool is already installed in the right place, just remove experimental tracking
                        upgraded++;
                    }
                }
            } catch (error) {
                debug.error(`[Experimental] Failed to check ${tool.org}/${tool.name}:`, error);
            }
        }

        if (upgraded > 0) {
            console.log(`✅ Upgraded ${upgraded} tools to stable versions`);
        } else {
            console.log('ℹ️  No experimental tools ready for upgrade');
        }
    }

    /**
     * Load experimental manifest
     */
    private async loadManifest(): Promise<ExperimentalManifest> {
        try {
            const content = await fs.readFile(this.manifestPath, 'utf-8');
            return JSON.parse(content);
        } catch {
            return {
                version: '1.0.0',
                experimental: [],
                lastUpdated: new Date().toISOString()
            };
        }
    }

    /**
     * Save experimental manifest
     */
    private async saveManifest(manifest: ExperimentalManifest): Promise<void> {
        await fs.mkdir(path.dirname(this.manifestPath), {recursive: true});
        await fs.writeFile(this.manifestPath, JSON.stringify(manifest, null, 2));
    }

    /**
     * Add tool to experimental manifest
     */
    private async addToManifest(tool: ExperimentalTool): Promise<void> {
        const manifest = await this.loadManifest();

        // Remove existing entry if present
        manifest.experimental = manifest.experimental.filter(
            t => !(t.org === tool.org && t.name === tool.name)
        );

        // Add new entry
        manifest.experimental.push(tool);
        manifest.lastUpdated = new Date().toISOString();

        await this.saveManifest(manifest);
    }

    /**
     * Remove tool from experimental manifest
     */
    private async removeFromManifest(tool: ExperimentalTool): Promise<void> {
        const manifest = await this.loadManifest();

        manifest.experimental = manifest.experimental.filter(
            t => !(t.org === tool.org && t.name === tool.name && t.version === tool.version)
        );

        manifest.lastUpdated = new Date().toISOString();
        await this.saveManifest(manifest);
    }
}

export function createExperimentalToolManager(options?: PackageManagerOptions): ExperimentalToolManager {
    return new ExperimentalToolManager(options);
}